var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import * as React from 'react';
import { DEFAULT_SIZE_CLASS } from './size-class';
import { useMediaQueries } from './use-media-queries';
import { layoutContextReducer } from './layout-context-state';
import { throttle } from 'throttle-debounce';
// avoid useLayoutEffect warning during SSR
var canUseDOM = !!(typeof window !== 'undefined' && window.document && window.document.createElement);
/* istanbul ignore next */
var useIsomorphicLayoutEffect = canUseDOM ? React.useLayoutEffect : React.useEffect;
var LayoutContext = React.createContext({
    baselineColumns: null,
    isInGrid: false,
    isInManagedLayout: false,
    sizeClass: DEFAULT_SIZE_CLASS,
    width: null,
    widthRef: { current: null }
});
/**
 * not public, exported for unit testing
 */
export var dispatchOnResize = function (dispatch, resizeObserverWidth) {
    return throttle(100, false, function () {
        dispatch({
            type: 'onResize',
            inputs: { resizeObserverWidth: resizeObserverWidth },
        });
    }, true)();
};
/**
 * convenience function for returning the nearest enclosing LayoutContext
 */
export var useLayoutContext = function () {
    var _a = React.useContext(LayoutContext), sizeClass = _a.sizeClass, baselineColumns = _a.baselineColumns, isInGrid = _a.isInGrid, isInManagedLayout = _a.isInManagedLayout;
    return {
        sizeClass: sizeClass,
        baselineColumns: baselineColumns,
        isInGrid: isInGrid,
        isInManagedLayout: isInManagedLayout
    };
};
export var LayoutContextProvider = function (_a) {
    var _b;
    var children = _a.children, baselineColumnsProp = _a.baselineColumns, deviceInformation = _a.deviceInformation, isInGridProp = _a.isInGrid, isInManagedLayoutProp = _a.isInManagedLayout, _c = _a.passThrough, passThroughProp = _c === void 0 ? false : _c, sizeClassProp = _a.sizeClass, _d = _a.useViewportMediaQueries, useViewportMediaQueriesProp = _d === void 0 ? false : _d, widthRefProp = _a.widthRef;
    var _e = useMediaQueries(useViewportMediaQueriesProp, deviceInformation), viewportMediaQuerySizeClass = _e.sizeClass, viewportMediaQueryBaselineColumns = _e.numBaselineColumns;
    var emptyWidthRef = React.useRef(null);
    var inheritedLayoutContext = React.useContext(LayoutContext);
    var _f = __read(React.useReducer(layoutContextReducer, {
        sizeClass: useViewportMediaQueriesProp
            ? viewportMediaQuerySizeClass
            : sizeClassProp !== null && sizeClassProp !== void 0 ? sizeClassProp : inheritedLayoutContext.sizeClass,
        baselineColumns: useViewportMediaQueriesProp
            ? viewportMediaQueryBaselineColumns
            : (_b = baselineColumnsProp !== null && baselineColumnsProp !== void 0 ? baselineColumnsProp : inheritedLayoutContext.baselineColumns) !== null && _b !== void 0 ? _b : null,
        isInGrid: isInGridProp !== null && isInGridProp !== void 0 ? isInGridProp : inheritedLayoutContext.isInGrid,
        widthRef: widthRefProp !== null && widthRefProp !== void 0 ? widthRefProp : emptyWidthRef,
        width: null,
        isInManagedLayout: isInManagedLayoutProp !== null && isInManagedLayoutProp !== void 0 ? isInManagedLayoutProp : !useViewportMediaQueriesProp
    }), 2), state = _f[0], dispatch = _f[1];
    var hasWindowObserverRef = React.useRef(false);
    var hasWindowObserver = function (value) {
        if (typeof value === 'boolean') {
            hasWindowObserverRef.current = value;
        }
        return hasWindowObserverRef.current;
    };
    var resizeObserverCallback = function (entries) {
        if (entries.length > 0) {
            dispatchOnResize(dispatch, entries[0].borderBoxSize[0].inlineSize);
        }
    };
    var windowResizeEventListenerCallback = function () {
        if (state.widthRef.current) {
            dispatchOnResize(dispatch, state.widthRef.current.offsetWidth);
        }
        else {
            dispatchOnResize(dispatch, window.innerWidth);
        }
    };
    var addWindowResizeEventListener = function () {
        if (!hasWindowObserver()) {
            window.addEventListener('resize', windowResizeEventListenerCallback);
            hasWindowObserver(true);
        }
    };
    var removeWindowResizeEventListener = function () {
        if (hasWindowObserver()) {
            window.removeEventListener('resize', windowResizeEventListenerCallback);
            hasWindowObserver(false);
        }
    };
    useIsomorphicLayoutEffect(function () {
        if (passThroughProp || useViewportMediaQueriesProp) {
            return;
        }
        var currentWidthRef = state.widthRef.current;
        var initialWidth = currentWidthRef ? currentWidthRef.offsetWidth : window.innerWidth;
        dispatchOnResize(dispatch, initialWidth);
        if (window.ResizeObserver && currentWidthRef) {
            removeWindowResizeEventListener();
            var resizeObserver_1 = new ResizeObserver(resizeObserverCallback);
            resizeObserver_1.observe(currentWidthRef);
            return function () {
                if (currentWidthRef) {
                    resizeObserver_1.unobserve(currentWidthRef);
                }
            };
        }
        else {
            addWindowResizeEventListener();
            return function () {
                removeWindowResizeEventListener();
            };
        }
    }, [state.widthRef.current, passThroughProp, useViewportMediaQueriesProp]);
    useIsomorphicLayoutEffect(function () {
        if (passThroughProp ||
            (state.baselineColumns === viewportMediaQueryBaselineColumns,
                state.sizeClass.value === viewportMediaQuerySizeClass.value)) {
            return;
        }
        dispatch({
            type: 'onMediaQueryMatchChange',
            inputs: {
                useViewportMediaQueries: useViewportMediaQueriesProp,
                viewportMediaQuerySizeClass: viewportMediaQuerySizeClass,
                viewportMediaQueryBaselineColumns: viewportMediaQueryBaselineColumns,
            },
        });
    }, [
        state.baselineColumns,
        state.sizeClass.value,
        useViewportMediaQueriesProp,
        viewportMediaQuerySizeClass,
        viewportMediaQueryBaselineColumns,
        passThroughProp,
    ]);
    useIsomorphicLayoutEffect(function () {
        if (passThroughProp || useViewportMediaQueriesProp)
            return;
        dispatch({
            type: 'onPropChange',
            inputs: {
                baselineColumns: baselineColumnsProp,
                isInGrid: isInGridProp,
                isInManagedLayout: isInManagedLayoutProp,
                sizeClass: sizeClassProp,
                widthRef: widthRefProp
            },
        });
    }, [baselineColumnsProp, isInGridProp, isInManagedLayoutProp, passThroughProp, sizeClassProp, useViewportMediaQueriesProp, widthRefProp]);
    return (React.createElement(LayoutContext.Provider, { value: passThroughProp ? inheritedLayoutContext : state },
        React.createElement(React.Fragment, null, children)));
};
//# sourceMappingURL=layout-context-provider.js.map